// Utility function to handle import failures with retry logic.  When a new deploy happens the old map page import is unavailable so will fail
//  So if it's unavailable just reload the page
import * as Sentry from "@sentry/react";
import { ImportError, RETRY_KEY } from "./LazyLoadErrorBoundary";

const MAX_RETRIES = 1;

export const importWithRetry = async <T,>(
  importFn: () => Promise<T>,
): Promise<T> => {
  const currentRetryCount = parseInt(
    sessionStorage.getItem(RETRY_KEY) || "0",
    10,
  );

  try {
    const result = await importFn();
    // Success - clear any existing retry count
    if (currentRetryCount > 0) {
      sessionStorage.removeItem(RETRY_KEY);
    }
    return result;
  } catch (error) {
    Sentry.captureMessage(
      "Failed to lazy load a component with name " + importFn.name,
      {
        level: "info",
      },
    );
    console.error("Import failed:", error);

    if (currentRetryCount < MAX_RETRIES) {
      // First failure - increment retry count and reload
      const newRetryCount = currentRetryCount + 1;
      sessionStorage.setItem(RETRY_KEY, newRetryCount.toString());

      console.log(
        "Import failed, attempting reload (attempt",
        newRetryCount,
        "of",
        MAX_RETRIES + 1,
        ")",
      );

      // Small delay to avoid immediate reload
      setTimeout(() => {
        window.location.reload();
      }, 100);

      // Return a promise that never resolves since we're reloading
      return new Promise(() => {});
    } else {
      // Max retries reached - return error component
      console.error(
        "Import failed after",
        MAX_RETRIES + 1,
        "attempts, showing error page",
      );
      sessionStorage.removeItem(RETRY_KEY);

      // Return a component that shows the error
      throw new ImportError("Failed to load page after multiple attempts");
    }
  }
};
